import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LookupItem } from "../models/LookupItem";
import { ViewModeItem, ViewModeItems } from "../models/ViewModeItem";
import { AvailableSortItems, SortItem } from "../models/SortItem";
import { FacetResult } from "../models/FacetResult";
import { SavingsBenefitsItem } from "../models/savingsBenefits/SavingsBenefitsItem";
import { SuggestItem } from '../models/SuggestItem';
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface SavingsBenefitsState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  selectedCategories: undefined | LookupItem[];
  selectedLocations: undefined | LookupItem[];
  selectedOrganizations: undefined | LookupItem[];
  selectedTags: undefined | LookupItem[];
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: SavingsBenefitsItem[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: SavingsBenefitsState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[1],
  keywords: "",
  suggestions: [],
  selectedCategories: undefined,
  selectedLocations: undefined,
  selectedOrganizations: undefined,
  selectedTags: undefined,
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  categories?: string[];
  locations?: string[];
  organizations?: string[];
  tags?: string[];
  path?: string;
  page: number;
  pageSize: number;
};

export const searchSavingsBenefits = createAsyncThunk<FacetResult<SavingsBenefitsItem>, SearchFilter>(
  "data/searchSavingsBenefits",
  async (payload: SearchFilter): Promise<FacetResult<SavingsBenefitsItem>> => {
    const response = await fetch("/savingbenefitssearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<SavingsBenefitsItem>;
  }
);

export const searchSavingsBenefitsSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/savingsBenefitsSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/savingbenefitssearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const savingsBenefitsSlice = createSlice({
  name: "savingsbenefits",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeCategories: (state, action: PayloadAction<undefined | LookupItem[]>) => {
      state.items = [];
      state.canLoadMore = false;
      state.selectedCategories = action.payload;
      state.page = 1;
    },
    changeLocations: (
      state,
      action: PayloadAction<undefined | LookupItem[]>
    ) => {
      state.items = [];
      state.selectedLocations = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeOrganizations: (state, action: PayloadAction<undefined | LookupItem[]>) => {
      state.items = [];
      state.page = 1;
      state.canLoadMore = false;
      state.selectedOrganizations = action.payload;
    },
    changeTags: (state, action: PayloadAction<undefined | LookupItem[]>) => {
      state.items = [];
      state.page = 1;
      state.canLoadMore = false;
      state.selectedTags = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords !== action.payload) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    clearFilters: (state, action: PayloadAction<undefined | LookupItem[]>) => {
      state.selectedCategories = undefined;
      state.selectedLocations = action.payload;
      state.selectedOrganizations = undefined;
      state.selectedTags = undefined;
      state.items = [];
      state.page = 1;
      state.canLoadMore = false;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchSavingsBenefits.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchSavingsBenefits.fulfilled,
        (state, action: PayloadAction<FacetResult<SavingsBenefitsItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchSavingsBenefits.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchSavingsBenefitsSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchSavingsBenefitsSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchSavingsBenefitsSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changeCategories,
  changeLocations,
  changeOrganizations,
  changeTags,
  changeKeywords,
  changePage,
  changeViewMode,
  clearFilters,
  changeInit,
  clearSuggestions
} = savingsBenefitsSlice.actions;

export default savingsBenefitsSlice.reducer;
